import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { uploadMediaFile } from '@/hooks/useUpload';
import { fetchPwaInstallStats, platformLabel, type PwaInstallStats } from '@/lib/pwaInstalls';
import { useAuth } from '@/hooks/useAuth';
import { logPlatformActivity } from '@/lib/platformActivity';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Réglages de l'application PWA.
// Permet de personnaliser l'apparence et les métadonnées de
// l'application installée (logo, nom, couleurs, mode d'affichage) et
// d'afficher les statistiques d'installation.
// ─────────────────────────────────────────────────────────────

interface PwaForm {
  name: string;
  shortName: string;
  description: string;
  logo: string | null;
  themeColor: string;
  backgroundColor: string;
  display: string;
}

const DISPLAY_OPTIONS = [
  { value: 'standalone', label: 'Standalone — plein écran applicatif (recommandé)' },
  { value: 'fullscreen', label: 'Plein écran total' },
  { value: 'minimal-ui', label: 'Interface minimale' },
  { value: 'browser', label: 'Onglet du navigateur' },
];

const EMPTY_FORM: PwaForm = {
  name: 'Zifek — Tableau de bord commerçant',
  shortName: 'Zifek',
  description: 'Gérez votre commerce Zifek : caisse, produits, services, crédit et commandes.',
  logo: null,
  themeColor: '#141414',
  backgroundColor: '#ffffff',
  display: 'standalone',
};

export default function SuperAdminPwaPage() {
  const { user } = useAuth();
  const [form, setForm] = useState<PwaForm>(EMPTY_FORM);
  const [rowId, setRowId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<PwaInstallStats>({ total: 0, last30: 0, byPlatform: {} });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await supabase
        .from('zifek')
        .select('id, title, lienimg, pwa_name, pwa_short_name, pwa_description, pwa_logo, pwa_theme_color, pwa_background_color, pwa_display')
        .limit(1)
        .maybeSingle();
      if (data) {
        const d = data as Record<string, unknown>;
        setRowId((d.id as number) ?? null);
        setForm({
          name: (d.pwa_name as string) || (d.title as string) || EMPTY_FORM.name,
          shortName: (d.pwa_short_name as string) || EMPTY_FORM.shortName,
          description: (d.pwa_description as string) || EMPTY_FORM.description,
          logo: (d.pwa_logo as string) || (d.lienimg as string) || null,
          themeColor: (d.pwa_theme_color as string) || EMPTY_FORM.themeColor,
          backgroundColor: (d.pwa_background_color as string) || EMPTY_FORM.backgroundColor,
          display: (d.pwa_display as string) || EMPTY_FORM.display,
        });
      }
      setStats(await fetchPwaInstallStats());
    } catch {
      /* garde les valeurs par défaut */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Seules les images sont acceptées (PNG, JPEG, WebP, SVG).');
      setTimeout(() => setError(null), 4000);
      return;
    }
    if (file.size > 1024 * 1024) {
      setError("L'image dépasse la limite de 1 Mo.");
      setTimeout(() => setError(null), 4000);
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const url = await uploadMediaFile(file, 'brand');
      setForm((prev) => ({ ...prev, logo: url }));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Échec de l'upload.");
      setTimeout(() => setError(null), 4000);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const payload = {
        pwa_name: form.name.trim() || EMPTY_FORM.name,
        pwa_short_name: form.shortName.trim() || EMPTY_FORM.shortName,
        pwa_description: form.description.trim(),
        pwa_logo: form.logo || '',
        pwa_theme_color: form.themeColor.trim() || '#141414',
        pwa_background_color: form.backgroundColor.trim() || '#ffffff',
        pwa_display: form.display,
      };
      if (rowId) {
        const { error: upErr } = await supabase.from('zifek').update(payload).eq('id', rowId);
        if (upErr) throw upErr;
      } else {
        const { error: insErr } = await supabase.from('zifek').insert(payload);
        if (insErr) throw insErr;
      }
      setSaved(true);
      void logPlatformActivity({
        actorId: user?.id, actorName: user?.name, actorRole: 'SuperAdmin',
        action: 'pwa.update',
        description: `Réglages de l'application PWA mis à jour (nom, logo, couleurs, mode d'affichage)`,
        targetType: 'pwa', targetId: rowId,
      });
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const platforms = Object.entries(stats.byPlatform).sort((a, b) => b[1] - a[1]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Application PWA</h2>
        <p className="text-sm text-foreground-500 mt-1">
          Personnalisez l&apos;application installable : logo d&apos;installation, nom, couleurs et mode d&apos;affichage.
        </p>
      </div>

      {/* Statistiques d'installation */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8 max-w-4xl">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <i className="ri-download-2-line text-lg text-primary-600"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{stats.total}</div>
          <div className="text-xs text-foreground-500 mt-0.5">Installations totales</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
          <i className="ri-calendar-line text-lg text-accent-600"></i>
          <div className="text-2xl font-bold font-heading text-foreground-950 mt-2">{stats.last30}</div>
          <div className="text-xs text-foreground-500 mt-0.5">30 derniers jours</div>
        </div>
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 col-span-2">
          <i className="ri-device-line text-lg text-secondary-600"></i>
          <div className="mt-2 space-y-1">
            {platforms.length === 0 ? (
              <div className="text-xs text-foreground-400">Aucune installation enregistrée pour l&apos;instant.</div>
            ) : (
              platforms.map(([key, count]) => (
                <div key={key} className="flex items-center justify-between text-xs">
                  <span className="text-foreground-600">{platformLabel(key)}</span>
                  <span className="font-semibold text-foreground-900">{count}</span>
                </div>
              ))
            )}
          </div>
          <div className="text-xs text-foreground-500 mt-2">Répartition par appareil</div>
        </div>
      </div>

      {/* Réglages */}
      <div className="max-w-3xl">
        <div className="bg-background-50 border border-background-200/70 rounded-lg p-5 space-y-5">
          {/* Logo */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Logo d&apos;installation</label>
            <p className="text-xs text-foreground-400 mb-3">Icône carrée (PNG/WebP/SVG), au moins 512×512, max 1 Mo.</p>
            <div className="flex items-start gap-4">
              <div className="w-20 h-20 rounded-2xl border border-background-200/70 bg-background-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                {uploading ? (
                  <i className="ri-loader-4-line animate-spin text-foreground-400 text-xl"></i>
                ) : form.logo ? (
                  <img src={form.logo} alt="Logo PWA" className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-smartphone-line text-foreground-300 text-xl"></i>
                    <span className="text-[9px] text-foreground-400">Aucun</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <label className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-foreground-100 text-foreground-700 text-xs font-medium cursor-pointer hover:bg-foreground-200/70 transition-colors whitespace-nowrap">
                  <i className="ri-upload-cloud-line text-sm"></i>
                  {form.logo ? "Changer le logo" : 'Uploader un logo'}
                  <input type="file" accept="image/jpeg,image/png,image/webp,image/svg+xml" onChange={handleUpload} className="hidden" />
                </label>
                {form.logo && (
                  <button
                    onClick={() => setForm((prev) => ({ ...prev, logo: null }))}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs text-foreground-500 hover:text-red-600 hover:bg-red-50/60 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-delete-bin-line text-xs"></i>
                    Retirer le logo
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Nom + nom court */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom de l&apos;application</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom court (icône)</label>
              <input
                type="text"
                value={form.shortName}
                onChange={(e) => setForm((prev) => ({ ...prev, shortName: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300"
              />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
              rows={2}
              className="w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 resize-none"
            />
          </div>

          {/* Couleurs + display */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Couleur de thème</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.themeColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, themeColor: e.target.value }))}
                  className="w-10 h-10 rounded-md border border-background-200/70 bg-background-50 cursor-pointer flex-shrink-0"
                />
                <input
                  type="text"
                  value={form.themeColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, themeColor: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm font-mono text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Couleur de fond</label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.backgroundColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-10 h-10 rounded-md border border-background-200/70 bg-background-50 cursor-pointer flex-shrink-0"
                />
                <input
                  type="text"
                  value={form.backgroundColor}
                  onChange={(e) => setForm((prev) => ({ ...prev, backgroundColor: e.target.value }))}
                  className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm font-mono text-foreground-950 focus:outline-none focus:border-primary-300"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Mode d&apos;affichage</label>
              <select
                value={form.display}
                onChange={(e) => setForm((prev) => ({ ...prev, display: e.target.value }))}
                className="w-full h-10 px-3 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer"
              >
                {DISPLAY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50/70 px-3 py-2 rounded-md flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>{error}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <button
              onClick={save}
              disabled={saving}
              className="h-10 px-5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin text-xs"></i>Enregistrement...</>
              ) : saved ? (
                <><i className="ri-check-line text-xs"></i>Enregistré</>
              ) : (
                <><i className="ri-save-line text-xs"></i>Enregistrer</>
              )}
            </button>
            <span className="text-[11px] text-foreground-400 flex items-center gap-1.5">
              <i className="ri-information-line"></i>
              Les utilisateurs verront le nouveau logo à leur prochaine installation.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}